// host.js — cuore della shell R1 "modalità ombra": riproduce ESATTAMENTE la semantica
// di workspace-host.js (attivazione/disattivazione workspace, classi body, boot-veil,
// bottoni sul rail admin, restore al refresh) sopra registry+bus+routeStore.
// Ogni funzione cita la riga di workspace-host.js di cui è il porting: la parità
// runtime è provata dal PROOF Playwright (evidence R1), quella del CSS dal test B2.4r.
//
// Differenze DICHIARATE rispetto a wsh (design §5, innesto 2):
//  - la MobileBar è React e monta SOLO ad auth risolta (B2.14) — wsh la creava
//    anche sulla schermata di login (sopra l'overlay, z 600 > z-50);
//  - localStorage: doppia scrittura shell_route + chiavi legacy (§7.3) via routeStore;
//  - dopo l'auth ensureButtons viene ritentato (kickEnsureButtons): con login lento
//    (>36s) i bottoni extra del rail non si perdevano il retry di wsh.

import { HOST_IDS, injectWshStyle, ensureRootRule, railEl, proxyRail, hideAmmRail, getUser } from './legacy-bridge.js';
import { label, moduleLabel, moduleSubtitle } from './glossary.js';
import { RAIL_ORDER, railVisible } from './registry.js';
import { railV2Enabled, submenuShellEnabled, lazyModulesEnabled } from './flags.js';
import { injectTokensStyle } from './tokens.css.js';

export function createShellHost(deps) {
  const { win, registry, bus, routeStore } = deps;
  const logger = deps.logger || console;
  const doc = win.document;

  let active = null; // id del workspace "extra" attivo, oppure null (= Procurement/Amministrazione)
  const subscribers = new Set(); // la MobileBar React si ridisegna a ogni notify()

  // ===== R2/R3: stato di rail e sottomenù =====
  const railOn = railV2Enabled(win);          // flag shell_rail_v2 (default ON, vedi flags.js)
  const submenuOn = submenuShellEnabled(win); // flag shell_submenu_v2 (dipende dal rail)
  const badges = {};       // module -> { itemId|'_': count } — accumulo di nav:badge (il replay del bus tiene solo l'ULTIMO evento)
  const itemByModule = {}; // module -> item corrente (da shell:navigate, per Submenu attivo + hash)
  const rawViewByModule = {}; // module -> vista RAW (payload.view: i detail non sono mascherati dal PARENT map)
  let authLogged = null;   // ultimo auth:changed visto (null = mai valutato)

  function isGuestId(id) { return HOST_IDS.indexOf(id) === -1; }

  function notify() {
    syncSubmenuBodyClass();
    subscribers.forEach((fn) => { try { fn(); } catch (e) { logger.error('[shell] subscriber', e); } });
  }
  function subscribe(fn) {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  }

  // ===== R2: modulo corrente e badge =====
  function currentModule() {
    if (active) return active;
    return doc.body && doc.body.classList.contains('workspace-amm') ? 'amm' : 'proc';
  }
  function currentItem(moduleId) {
    return itemByModule[moduleId || currentModule()] || null;
  }
  function moduleBadge(id) {
    const m = badges[id];
    if (!m) return 0;
    let n = 0;
    for (const k of Object.keys(m)) n += m[k] || 0;
    return n;
  }
  function itemBadge(moduleId, itemId) {
    const m = badges[moduleId];
    return (m && m[itemId]) || 0;
  }

  // ===== R3: dati del sottomenù shell (null = colonna collassata) =====
  // Solo il modulo attivo, SOLO se registrato v2 con submenu:'shell' e nav non
  // vuota, SOLO a flag attiva: ogni altro caso tiene la nav interna del modulo.
  // Ondata 6: porta anche titolo (glossario, fallback label di registrazione —
  // mai stringa vuota) e sottotitolo (glossario <id>.subtitle; assente = '')
  // per l'header di contesto — desktop e sheet mobile leggono GLI STESSI dati.
  function submenuData() {
    if (!submenuOn) return null;
    const id = currentModule();
    const w = registry.get(id);
    if (!w || w.submenu !== 'shell' || !Array.isArray(w.nav) || w.nav.length === 0) return null;
    return {
      id,
      nav: w.nav,
      activeItem: currentItem(id),
      title: moduleLabel(id, w.label),
      subtitle: moduleSubtitle(id),
    };
  }

  // flag CSS del monolite (§7.2): body.shell-submenu nasconde #sidebar SOLO quando
  // il sottomenù shell è attivo davvero (e l'utente è loggato: sulla schermata di
  // login il monolite resta intonso, B2.14). Classe tolta ⇒ #sidebar ricompare.
  function syncSubmenuBodyClass() {
    if (!doc.body) return;
    doc.body.classList.toggle('shell-submenu', authLogged !== false && !!submenuData());
  }

  // ===== BOOT-VEIL (wsh:18-27 VERBATIM) =====
  // Se al refresh va ripristinato un workspace diverso da Procurement, nascondi tutto
  // (rail + contenuti) finché non è settato lo stato finale → niente selettori che ballano.
  let _needsRestore = false;
  try {
    _needsRestore = !!win.localStorage.getItem('wsh_active') || win.localStorage.getItem('amm_workspace') === 'amm';
  } catch (e) { /* localStorage negato: si parte senza veil */ }

  function endBooting() {
    if (doc.body) doc.body.classList.remove('wsh-booting');
    try { doc.documentElement.classList.remove('wsh-prebooting'); } catch (e) { /* preludio assente */ }
  }

  // ===== ROOT & BOTTONI (wsh:185-263 VERBATIM, parametrizzato) =====
  function hideRoot(id) {
    const r = doc.getElementById(id + '-root');
    if (r) r.style.display = 'none';
  }

  function syncButtons() {
    const rail = railEl(doc); if (!rail) return;
    rail.querySelectorAll('.amm-rail-btn[data-ws]').forEach((b) => {
      if (active) b.classList.remove('active');
    });
    rail.querySelectorAll('.wsh-btn').forEach((b) => {
      b.classList.toggle('active', b.getAttribute('data-wsh') === active);
    });
  }

  // Aggiunge i bottoni dei workspace registrati al rail e aggancia i bottoni host
  // (Proc/Amm) per spegnere l'extra quando si torna su Procurement/Amministrazione.
  function ensureButtons() {
    const rail = railEl(doc); if (!rail) return false;
    registry.list().forEach((w) => {
      if (!isGuestId(w.id)) return; // proc/amm vivono già sul rail admin
      if (rail.querySelector('.wsh-btn[data-wsh="' + w.id + '"]')) return;
      const b = doc.createElement('div');
      b.className = 'amm-rail-btn wsh-btn';
      b.setAttribute('data-wsh', w.id);
      b.title = moduleLabel(w.id, w.label);
      b.textContent = w.icon || (w.label || w.id).slice(0, 4); // parità wsh:253 (icona/label di registrazione)
      b.onclick = function () { activate(w.id); };
      rail.appendChild(b);
    });
    // hook (una sola volta) sui bottoni host: tornando su Proc/Amm si disattiva l'extra
    rail.querySelectorAll('.amm-rail-btn[data-ws]').forEach((b) => {
      if (b.__wshHooked) return;
      b.__wshHooked = true;
      b.addEventListener('click', () => {
        clearHostQueue(); // F3.3: il dito dell'utente è l'intenzione più recente — la coda decade
        if (active) deactivate();
        // admin-module (onclick, registrato PRIMA) ha già commutato workspace-amm:
        // qui si legge lo stato finale e si scrive la route (§7.3).
        const mod = doc.body.classList.contains('workspace-amm') ? 'amm' : 'proc';
        routeStore.write({ module: mod });
        bus.emit('shell:module-changed', { id: mod, prev: null });
        notify(); // updateModbar di wsh:261
      }, false);
    });
    return true;
  }

  let _buttonsRetryTimer = null;
  function ensureButtonsWithRetry() {
    if (ensureButtons()) return;
    if (_buttonsRetryTimer) return;
    // il rail (admin-module) potrebbe non esserci ancora: attendi (wsh:272-276)
    let n = 0;
    _buttonsRetryTimer = win.setInterval(() => {
      if (ensureButtons() || ++n > 120) { win.clearInterval(_buttonsRetryTimer); _buttonsRetryTimer = null; }
    }, 300);
  }
  // post-auth: il rail admin appare col token — un giro in più non costa nulla
  function kickEnsureButtons() { ensureButtonsWithRetry(); }

  // ===== ATTIVAZIONE (wsh:213-240 VERBATIM + routeStore/bus) =====
  function deactivate() {
    const prev = active;
    if (active && registry.has(active)) {
      const w = registry.get(active);
      const hide = typeof w.unmount === 'function' ? w.unmount : w.onHide; // v2 con fallback v1 (§3)
      if (typeof hide === 'function') { try { hide.call(w); } catch (e) { logger.warn('[wsh] onHide', e); } }
      hideRoot(active); // robustezza: nascondi comunque il root del modulo
      doc.body.classList.remove('wsh-' + active);
    }
    active = null;
    routeStore.write({ module: doc.body.classList.contains('workspace-amm') ? 'amm' : 'proc' }); // rimuove wsh_active (parità wsh:220)
    doc.body.classList.remove('wsh-active');
    if (prev) bus.emit('shell:module-changed', { id: doc.body.classList.contains('workspace-amm') ? 'amm' : 'proc', prev });
    notify(); // closeModsheet + updateModbar di wsh:222
    syncButtons();
  }

  function activateGuest(id) {
    const w = registry.get(id); if (!w) return; // parità wsh:227 (id ignoto = no-op)
    const prev = active || (doc.body.classList.contains('workspace-amm') ? 'amm' : 'proc');
    // esci dai workspace host (Amministrazione) e da eventuali altri extra
    doc.body.classList.remove('workspace-amm');
    registry.list().forEach((k) => {
      if (k.id !== id && isGuestId(k.id)) { hideRoot(k.id); doc.body.classList.remove('wsh-' + k.id); }
    });
    doc.body.classList.add('wsh-active');
    doc.body.classList.add('wsh-' + id); // mostra SOLO la root di questo workspace (CSS bulletproof)
    active = id;
    routeStore.write({ module: id }); // wsh_active=id + shell_route (§7.3)
    const show = typeof w.mount === 'function'
      ? () => w.mount(doc.getElementById(id + '-root'), { bus })
      : w.onShow; // v1 (tutti gli ospiti attuali)
    if (typeof show === 'function') {
      try { show.call(w); } catch (e) { logger.warn('[wsh] onShow', e); }
    }
    bus.emit('shell:module-changed', { id, prev });
    notify(); // closeModsheet + updateModbar di wsh:238
    syncButtons();
  }

  // proc/amm: attivazione via click programmatico sul rail admin (legacy-bridge).
  // Il click fa girare admin-module (workspace-amm, amm_workspace) e il nostro hook
  // sul bottone (deactivate + routeStore) — stesso percorso del dito dell'utente.
  //
  // F3.3 (ondata 16, segnalazione ondata 11): nel primo ~1s post-login #amm-rail
  // NON esiste ancora (nasce col token, admin-module) → proxyRail ritorna false e
  // il click dell'utente moriva in SILENZIO. Contratto nuovo: l'intento si ACCODA
  // (slot UNICO: l'ultima intenzione vince, mai doppio click) e si riesegue al
  // poll finché il rail nasce; oltre il tetto si rinuncia RUMOROSAMENTE (toast).
  // itemId/params del navigate NON passano di qui: applyItem gira subito e gli
  // adapter proc/amm hanno già la LORO retry (clickBridgeRetry / showView del
  // monolite) — la coda copre SOLO il buco del rail. Contract test:
  // server/src/lib/shell-host-activate.test.js.
  const HOST_QUEUE_POLL_MS = 150;  // stesso passo dei poll gemelli (restoreActive, enforceHostRestore)
  const HOST_QUEUE_MAX_TRIES = 53; // ≈8s: oltre, il rail non nascerà più (boot rotto) → si dichiara
  let hostQueue = null;            // { id, tries, timer } — un solo slot di coda

  function clearHostQueue() {
    if (!hostQueue) return;
    win.clearInterval(hostQueue.timer);
    hostQueue = null;
  }

  function queueHostIntent(id) {
    clearHostQueue(); // slot unico: l'ultima intenzione vince
    const q = { id, tries: 0, timer: 0 };
    q.timer = win.setInterval(() => {
      if (proxyRail(doc, q.id)) { clearHostQueue(); return; }
      if (++q.tries >= HOST_QUEUE_MAX_TRIES) {
        clearHostQueue();
        logger.warn('[shell] host "' + q.id + '" mai attivato: #amm-rail non comparso entro il tetto (' + HOST_QUEUE_MAX_TRIES + ' tentativi)');
        bus.emit('toast', { kind: 'error', msg: label('shell.host.unreachable') + moduleLabel(q.id) });
      }
    }, HOST_QUEUE_POLL_MS);
    hostQueue = q;
  }

  function activateHost(id) {
    if (active) deactivate();
    if (!proxyRail(doc, id)) queueHostIntent(id);
  }

  // R7: ospite NON registrato + loader disponibile = modulo LAZY → outlet
  // loading, loadModule (gruppo dal manifest servito), poi attivazione vera.
  // Stati B2.18: loading (spinner) / errore con Riprova (outlet, mai schermo
  // bianco: sotto resta la vista corrente). Id ignoto anche al manifest ⇒
  // toast rumoroso (parità col no-op wsh, ma dichiarata). Ritorna una Promise.
  function lazyActivate(id) {
    const outlet = deps.outlet;
    if (outlet) outlet.showLoading(id);
    notify(); // chiude modsheet/secsheet mobile subito (come un activate riuscito)
    return deps.loader.loadModule(id).then(() => {
      if (outlet) outlet.hide();
      activateGuest(id);
    }).catch((e) => {
      const unknown = /non presente tra i lazy/.test((e && e.message) || '');
      if (unknown) {
        if (outlet) outlet.hide();
        bus.emit('toast', { kind: 'error', msg: label('shell.loader.unknown') + moduleLabel(id) });
        return;
      }
      if (outlet) outlet.showError(id, e, () => activate(id)); // Riprova = stesso percorso
      else logger.error('[shell] lazy activate ' + id, e);
    });
  }

  function activate(id) {
    clearHostQueue(); // F3.3: nuova intenzione — quella eventualmente in coda decade (l'ultima vince)
    if (HOST_IDS.indexOf(id) !== -1) { activateHost(id); return undefined; }
    if (!registry.has(id) && deps.loader) return lazyActivate(id);
    activateGuest(id);
    return undefined;
  }

  // Un modulo HOST (proc/amm) è "attivo ADESSO" quando nessun ospite è davanti
  // (active null) e la classe body corrisponde: amm ⇒ body.workspace-amm, proc ⇒
  // il suo complemento (stesso predicato di moduleList activeNow, r.326).
  function hostActiveNow(id) {
    if (HOST_IDS.indexOf(id) === -1) return false;
    if (active) return false; // un ospite (es. Contabilità) è davanti: nessun host è attivo
    const isAmm = doc.body.classList.contains('workspace-amm');
    return id === 'amm' ? isAmm : !isAmm;
  }

  // Workspaces.navigate(moduleId, itemId?, params?) — API v2 (§3).
  // R7: se activate è lazy (Promise) la voce si applica DOPO la registrazione;
  // fallimento di caricamento = l'outlet ha già mostrato l'errore, niente item.
  function navigate(moduleId, itemId, params) {
    function applyItem() {
      if (!itemId) return;
      const w = registry.get(moduleId);
      if (w && typeof w.navigate === 'function') {
        try { w.navigate(itemId, params); } catch (e) { logger.error('[shell] navigate ' + moduleId + '/' + itemId, e); }
      }
      bus.emit('shell:navigate', { module: moduleId, item: itemId, params });
    }
    // o35 (ondata 35) — RAZZA S1: se stiamo navigando a una VOCE di un modulo host
    // GIÀ attivo, ri-attivarlo (activate → activateHost → proxyRail → click sul rail
    // → admin-module setWorkspace → renderPage(currentPage STANTIO)) è ridondante e
    // introduce un renderPage stantio in volo PRIMA della voce (doppio renderPage,
    // pagina sbagliata se una fetch arriva fuori ordine). Con l'host già attivo si va
    // DIRITTI alla voce: un solo renderPage (la destinazione). NON tocca admin-module.js.
    // La guardia richiede itemId: un activate esplicito (nav senza voce, click sul
    // rail) resta invariato.
    if (itemId && hostActiveNow(moduleId)) { applyItem(); return; }
    const r = activate(moduleId);
    if (r && typeof r.then === 'function') r.then(() => { if (registry.has(moduleId)) applyItem(); });
    else applyItem();
  }

  // ===== MOBILEBAR: dati e azioni (wsh:104-183, la resa è React) =====
  function currentModuleLabel() {
    if (active && registry.has(active)) {
      const w = registry.get(active);
      return moduleLabel(active, w.label); // 'cont' → 'Contabilità' via glossario (era l'hardcode wsh:112)
    }
    if (doc.body.classList.contains('workspace-amm')) return moduleLabel('amm');
    return moduleLabel('proc');
  }

  // Elenco del modsheet: bottoni host del rail (Proc/Amm) + ospiti registrati (wsh:148-162).
  // Con rail v2 attivo (R2) l'elenco esce in RAIL_ORDER (Cruscotto in testa, 6 pari
  // grado, badge dal bus) — a flag spenta resta l'ordine legacy (parità R1).
  function moduleList() {
    const rail = railEl(doc); const out = [];
    if (rail) {
      rail.querySelectorAll('.amm-rail-btn[data-ws]').forEach((b) => {
        const ws = b.getAttribute('data-ws');
        const isAmm = doc.body.classList.contains('workspace-amm');
        out.push({
          moduleId: ws,
          label: moduleLabel(ws, b.textContent || ws),
          el: b,
          key: 'host-' + ws,
          activeNow: !active && (ws === 'amm' ? isAmm : !isAmm),
          badge: moduleBadge(ws),
        });
      });
    }
    registry.list().forEach((w) => {
      if (!isGuestId(w.id)) return; // proc/amm già presenti come bottoni host
      out.push({ moduleId: w.id, label: moduleLabel(w.id, w.label), id: w.id, key: 'guest-' + w.id, activeNow: active === w.id, badge: moduleBadge(w.id) });
    });
    // R7: gli ospiti LAZY non ancora registrati devono comunque comparire nel
    // modsheet mobile (il Rail desktop rende già RAIL_ORDER fisso): voce
    // sintetica dal glossario, select → activate → loader. Solo con loader
    // presente (parità R1 nei contesti senza lazy).
    if (deps.loader) {
      RAIL_ORDER.forEach((id) => {
        if (!isGuestId(id) || registry.has(id)) return;
        if (out.some((m) => m.moduleId === id)) return;
        if (!railVisible(id)) return; // modulo gated non ancora acceso: fuori dal modsheet
        out.push({ moduleId: id, label: moduleLabel(id), id, key: 'lazy-' + id, activeNow: false, badge: moduleBadge(id) });
      });
    }
    if (!railOn) return out;
    const rank = (m) => { const i = RAIL_ORDER.indexOf(m.moduleId); return i === -1 ? RAIL_ORDER.length : i; };
    return out.slice().sort((a, b) => rank(a) - rank(b));
  }

  // Riga del modsheet toccata (wsh:173-177 VERBATIM).
  function select(m) {
    if (m.id) { activate(m.id); }
    else if (m.el) { if (active) deactivate(); m.el.click(); }
  }

  // La metà DESTRA apre il menu SEZIONI del modulo corrente cliccandone l'hamburger
  // (ora nascosto) — wsh:142-147. FALLBACK: usato SOLO quando il modulo non ha il
  // sottomenù shell (host.submenuData null); di norma le sezioni si aprono dallo
  // sheet React (MobileBar.onSectionsTap). T3 (2026-07-17): la .mobile-topbar è
  // nascosta su mobile (display:none) ma resta nel DOM — il selettore NON è più
  // legato a quel contenitore (cerca l'hamburger "Sezioni" ovunque nella root del
  // modulo) e .click() scatta comunque su elemento nascosto: le sezioni si aprono.
  function openSectionMenu() {
    let t = null;
    if (active) t = doc.querySelector('#' + active + '-root .hb[aria-label="Sezioni"]');
    if (!t && !doc.body.classList.contains('workspace-amm')) t = doc.getElementById('nav-toggle');
    if (t) t.click();
  }

  // ===== RESTORE AL REFRESH (wsh:287-303 VERBATIM + §7.3 + hash R2/§8 tempo 1) =====
  // R2: la route di boot arriva da deps.bootRoute (hash > shell_route > wsh_active
  // legacy, router.initialRoute). Senza bootRoute (test/parità R1) vale routeStore.
  function restoreActive() {
    const route = typeof deps.bootRoute === 'function' ? deps.bootRoute() : null;
    const saved = route ? (isGuestId(route.module) ? route.module : null)
      : routeStore.restoreTarget(); // shell_route || wsh_active (§7.3)
    if (route && !saved) {
      // Host esplicito (proc/amm) da hash/route: allinea admin-module se il suo
      // restore (localStorage amm_workspace) dice altro — stesso percorso del dito.
      enforceHostRestore(route.module);
    }
    if (route && route.module === 'proc' && route.item) dispatchBootItem(route);
    if (!saved || !isGuestId(saved)) {
      // Era Procurement o Amministrazione: admin-module ha già ripristinato. Rivela appena pronto.
      if (win.requestAnimationFrame) win.requestAnimationFrame(endBooting); else endBooting();
      return;
    }
    // R7: se l'ospite da ripristinare è LAZY (non nel manifest eager) il poll
    // sotto non lo vedrebbe MAI: si scalda subito il gruppo dal loader.
    // Fallimento = outlet errore con Riprova (il poll scade e rivela la UI:
    // l'utente resta su Procurement, mai schermo nascosto).
    if (deps.loader && !registry.has(saved)) {
      deps.loader.loadModule(saved).catch((e) => {
        if (deps.outlet && !/non presente tra i lazy/.test((e && e.message) || '')) {
          deps.outlet.showError(saved, e, () => activate(saved));
        }
      });
    }
    let n = 0;
    const t = win.setInterval(() => {
      n++;
      if (registry.has(saved) && railEl(doc)) { win.clearInterval(t); activate(saved); endBooting(); }
      else if (n > 140) { win.clearInterval(t); endBooting(); } // resa: non lasciare la UI nascosta
    }, 150);
  }

  // Boot su #/proc o #/amm: quando il rail admin esiste, se il workspace ripristinato
  // da admin-module non coincide con la route, click programmatico (pattern wsh).
  function enforceHostRestore(id) {
    if (HOST_IDS.indexOf(id) === -1) return;
    let n = 0;
    const t = win.setInterval(() => {
      n++;
      if (railEl(doc)) {
        win.clearInterval(t);
        const actual = doc.body.classList.contains('workspace-amm') ? 'amm' : 'proc';
        if (!active && actual !== id) proxyRail(doc, id);
      } else if (n > 140) { win.clearInterval(t); }
    }, 150);
  }

  // Boot su #/proc/<vista>: il monolite ripristina la sua crm_ui_view per conto
  // suo (init post-DCL) e l'ORDINE dei suoi showView di boot non è garantito.
  // Strategia robusta: debounce sull'ULTIMO shell:navigate (quando la tempesta di
  // boot si è assestata, ~700ms di quiete) e SOLO allora si impone la vista
  // dell'hash se diversa. Il confronto usa la vista RAW: #/proc/suppliers deve
  // forzare la LISTA anche se il restore è finito su supplier-detail (che il
  // PARENT map maschera come 'suppliers'). Best-effort DICHIARATO: massimo 2
  // tentativi (mai lotta con l'utente), cutoff assoluto 12s, mai throw.
  function dispatchBootItem(route) {
    let done = false;
    let attempts = 0;
    let timer = null;
    function finish() {
      if (done) return;
      done = true;
      off();
      if (timer) win.clearTimeout(timer);
      win.clearTimeout(idleKick);
      win.clearTimeout(cutoff);
    }
    function settle() {
      if (done) return;
      if (currentModule() !== 'proc') { finish(); return; }
      if (rawViewByModule.proc === route.item) { finish(); return; }
      if (++attempts > 2) { finish(); return; }
      navigate('proc', route.item, route.params); // riemette shell:navigate → schedule → settle → raw==item → finish
    }
    function schedule() {
      if (done) return;
      if (timer) win.clearTimeout(timer);
      timer = win.setTimeout(settle, 700);
    }
    const off = bus.on('shell:navigate', schedule, { replay: false });
    const idleKick = win.setTimeout(settle, 8000); // nessun navigate di boot? si tenta comunque una volta
    const cutoff = win.setTimeout(finish, 12000);
  }

  // ===== BOOT =====
  function boot() {
    injectWshStyle(doc);
    injectTokensStyle(doc); // R2/R3: token + CSS rail/sottomenù (inerte senza classi flag)
    if (railOn) hideAmmRail(doc, true); // flag ON: body.shell-rail-v2 (il CSS spegne #amm-rail)
    if (_needsRestore && doc.body) doc.body.classList.add('wsh-booting');
    // R2: accumulo badge dal bus (il replay del bus tiene solo l'ULTIMO nav:badge:
    // il rail nato dopo deve vedere TUTTE le voci → si accumula qui, si legge da host)
    bus.on('nav:badge', (p) => {
      if (!p || typeof p.module !== 'string') return;
      const m = badges[p.module] || (badges[p.module] = {});
      const key = p.item || '_';
      const c = Number(p.count) || 0;
      if (m[key] === c) return;
      m[key] = c;
      notify();
    }, { replay: true });
    // R3: vista corrente per modulo (Submenu attivo + hash reflect + shell_route con item)
    bus.on('shell:navigate', (p) => {
      if (!p || typeof p.module !== 'string' || !p.item) return;
      if (p.module !== currentModule()) return; // navigazioni in background: non è la vista visibile
      rawViewByModule[p.module] = p.view || p.item;
      if (itemByModule[p.module] === p.item) return;
      itemByModule[p.module] = p.item;
      routeStore.write({ module: p.module, item: p.item }); // §7.3, ora con item
      notify();
    }, { replay: false });
    // logout: mai lasciare #sidebar nascosto sotto la schermata di login (B2.14)
    bus.on('auth:changed', (p) => {
      authLogged = !!(p && p.logged);
      syncSubmenuBodyClass();
    }, { replay: true });
    // a ogni registrazione di un ospite: stile, regola root bulletproof, bottone sul rail
    registry.onChange((kind, entry) => {
      if (isGuestId(entry.id)) {
        injectWshStyle(doc);
        ensureRootRule(doc, entry.id);
        ensureButtonsWithRetry();
      }
      notify();
    });
    restoreActive();
    // R7 kill-switch per-browser: localStorage.shell_lazy='0' ⇒ tutti i gruppi
    // lazy si caricano SUBITO (resa quasi-eager senza redeploy, flags.js).
    if (deps.loader && !lazyModulesEnabled(win)) {
      deps.loader.preloadAll().catch((e) => logger.error('[shell] preloadAll (shell_lazy=0)', e));
    }
    // Sicurezza: la UI non deve MAI restare nascosta (wsh:303 VERBATIM).
    win.setTimeout(endBooting, 5000);
  }

  return {
    boot,
    activate,
    deactivate,
    navigate,
    active: () => active,
    subscribe,
    moduleList,
    select,
    currentModuleLabel,
    openSectionMenu,
    kickEnsureButtons,
    endBooting,
    // R2/R3
    railV2On: () => railOn,
    currentModule,
    currentItem,
    moduleBadge,
    itemBadge,
    submenuData,
    user: () => getUser(win),
  };
}
